import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Card,
  Tabs,
  Table,
  Tag,
  Button,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Alert,
  Descriptions,
  Badge,
} from 'antd'
import {
  CloudUploadOutlined,
  ReloadOutlined,
  SettingOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import ProTable, { ProColumns } from '@ant-design/pro-table'
import { deviceApi } from '@api/devices'
import { formatTime } from '@utils/format'
import type { Device, OTAConfig } from '@app-types/device'
import styles from './index.module.css'

const { TabPane } = Tabs

const OTA: React.FC = () => {
  const [configForm] = Form.useForm()
  const [isMobile, setIsMobile] = useState(false)

  // 监听窗口大小变化
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const { data: otaConfig, refetch: refetchConfig } = useQuery({
    queryKey: ['ota-config'],
    queryFn: deviceApi.getOTAConfig,
  })

  const { data: otaRecords, refetch: refetchRecords } = useQuery({
    queryKey: ['ota-records'],
    queryFn: () => deviceApi.getOTARecords({ limit: 100 }),
  })

  const { mutate: updateConfig } = useMutation({
    mutationFn: (config: Partial<OTAConfig>) => deviceApi.updateOTAConfig(config),
    onSuccess: () => {
      message.success('OTA配置已更新')
      refetchConfig()
    },
  })

  const { mutate: upgradeDevice } = useMutation({
    mutationFn: (deviceId: string) => deviceApi.upgradeDevice(deviceId),
    onSuccess: (data) => {
      if (data.upgrade_started) {
        message.success(data.message)
      } else {
        message.info(data.message)
      }
    },
  })

  const { mutate: upgradeAllDevices } = useMutation({
    mutationFn: () => deviceApi.upgradeAllDevices(),
    onSuccess: (data) => {
      message.success(`已启动 ${data.upgraded.length} 台设备的升级，跳过 ${data.skipped.length} 台`)
    },
  })

  const handleConfigSubmit = (values: any) => {
    updateConfig(values)
  }

  const deviceColumns = useMemo<ProColumns<Device>[]>(() => {
    const cols: ProColumns<Device>[] = [
      {
        title: '设备ID',
        dataIndex: 'device_id',
        copyable: !isMobile,
        ellipsis: true,
        width: isMobile ? 130 : 200,
        fixed: isMobile ? false : 'left',
      },
    ]
    
    if (!isMobile) {
      cols.push(
        {
          title: '当前版本',
          dataIndex: 'firmware_version',
          render: (_, record) => record.firmware_version || 'N/A',
          width: 100,
        },
        {
          title: '目标版本',
          dataIndex: 'target_version',
          render: (_, record) => record.target_version || otaConfig?.target_version || 'N/A',
          width: 100,
        }
      )
    }
    
    cols.push(
      {
        title: isMobile ? '状态' : '在线状态',
        dataIndex: 'is_online',
        width: isMobile ? 70 : 100,
        render: (_, record) => <Badge status={record.is_online ? 'success' : 'error'} text={record.is_online ? (isMobile ? '' : '在线') : (isMobile ? '' : '离线')} />,
      },
      {
        title: '升级状态',
        dataIndex: 'upgrade_status',
        width: isMobile ? 80 : 100,
        render: (_, record) => {
          const val = record.upgrade_status
          const statusMap: Record<string, { color: string; text: string }> = {
            idle: { color: 'default', text: isMobile ? '空' : '空闲' },
            pending: { color: 'processing', text: isMobile ? '等' : '等待' },
            downloading: { color: 'processing', text: isMobile ? '下' : '下载' },
            applying: { color: 'warning', text: isMobile ? '升' : '升级' },
            success: { color: 'success', text: isMobile ? '成' : '成功' },
            failed: { color: 'error', text: isMobile ? '败' : '失败' },
          }
          const { color, text } = statusMap[val as string] || { color: 'default', text: val }
          return <Tag color={color}>{text}</Tag>
        },
      }
    )
    
    if (!isMobile) {
      cols.push(
        {
          title: '自动升级',
          dataIndex: 'auto_upgrade_enabled',
          render: (_, record) => <Switch checked={record.auto_upgrade_enabled} disabled size="small" />,
          width: 90,
        },
        {
          title: '最后升级',
          dataIndex: 'last_upgrade_at',
          render: (_, record) => formatTime(record.last_upgrade_at),
          width: 180,
        }
      )
    }
    
    cols.push({
      title: '操作',
      valueType: 'option',
      width: isMobile ? 50 : 100,
      fixed: isMobile ? false : 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<CloudUploadOutlined />}
          onClick={() => upgradeDevice(record.device_id)}
          disabled={!record.is_online}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '' : '升级'}
        </Button>
      ),
    })
    
    return cols
  }, [isMobile, otaConfig?.target_version])

  const recordColumns = [
    { title: '设备ID', dataIndex: 'device_id', copyable: true },
    { title: '起始版本', dataIndex: 'from_version', render: (v: string | null) => v || '-' },
    { title: '目标版本', dataIndex: 'to_version', render: (v: string | null) => v || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      render: (s: string) => {
        const colorMap: Record<string, string> = {
          success: 'success',
          failed: 'error',
          downloading: 'processing',
          applying: 'warning',
          pending: 'default',
        }
        const textMap: Record<string, string> = {
          success: '成功',
          failed: '失败',
          downloading: '下载中',
          applying: '升级中',
          pending: '等待中',
        }
        return <Tag color={colorMap[s] || 'default'}>{textMap[s] || s}</Tag>
      },
    },
    { title: '消息', dataIndex: 'message', ellipsis: true },
    { title: '开始时间', dataIndex: 'started_at', render: (t: string) => formatTime(t) },
    { title: '完成时间', dataIndex: 'completed_at', render: (t: string | null) => formatTime(t) },
  ]

  return (
    <div className={styles.container}>
      <h1>OTA 固件升级</h1>
      
      <Tabs defaultActiveKey="devices">
        <TabPane
          tab={<span><CloudUploadOutlined /> 设备升级</span>}
          key="devices"
        >
          <Alert
            message="批量升级"
            description="点击下方的'批量升级'按钮将同时启动所有需要升级的设备。建议在网络空闲时执行。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Popconfirm
                title="确定要批量升级所有设备吗？"
                onConfirm={() => upgradeAllDevices()}
              >
                <Button type="primary" icon={<CloudUploadOutlined />} size={isMobile ? 'small' : 'middle'}>
                  {isMobile ? '批量' : '批量升级'}
                </Button>
              </Popconfirm>
            }
          />
          
          <ProTable<Device>
            headerTitle="设备列表"
            rowKey="device_id"
            columns={deviceColumns}
            request={async () => {
              const res = await deviceApi.getDevices()
              return {
                data: res.devices,
                success: true,
                total: res.total,
              }
            }}
            pagination={{ pageSize: isMobile ? 10 : 20 }}
            search={isMobile ? false : { labelWidth: 120 }}
            scroll={{ x: isMobile ? 350 : 800 }}
          />
        </TabPane>

        <TabPane
          tab={<span><HistoryOutlined /> 升级记录</span>}
          key="records"
        >
          <Card
            extra={
              <Button icon={<ReloadOutlined />} onClick={() => refetchRecords()} size={isMobile ? 'small' : 'middle'}>
                {isMobile ? '' : '刷新'}
              </Button>
            }
          >
            <Table
              dataSource={otaRecords?.records || []}
              columns={recordColumns}
              rowKey="id"
              pagination={{ pageSize: 20 }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={<span><SettingOutlined /> OTA配置</span>}
          key="config"
        >
          <Card title="全局OTA配置">
            <Form
              form={configForm}
              onFinish={handleConfigSubmit}
              layout="vertical"
              initialValues={otaConfig}
            >
              <Form.Item
                name="ota_url"
                label="OTA固件地址"
                rules={[{ required: true, message: '请输入OTA固件地址' }]}
              >
                <Input placeholder="http://ota.starlake.tech:48888/system_patch.bin" />
              </Form.Item>
              
              <Form.Item
                name="target_version"
                label="目标版本"
                rules={[{ required: true, message: '请输入目标版本' }]}
              >
                <Input placeholder="1.0.3" />
              </Form.Item>
              
              <Form.Item
                name="auto_upgrade_enabled"
                label="自动升级"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block={isMobile} size={isMobile ? 'middle' : 'middle'}>
                  保存配置
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="当前配置" style={{ marginTop: 16 }}>
            <Descriptions bordered column={isMobile ? 1 : 3}>
              <Descriptions.Item label="OTA固件地址">{otaConfig?.ota_url}</Descriptions.Item>
              <Descriptions.Item label="目标版本">{otaConfig?.target_version}</Descriptions.Item>
              <Descriptions.Item label="自动升级">{otaConfig?.auto_upgrade_enabled ? '开启' : '关闭'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  )
}

export default OTA
